import { z } from "zod";

import { request, setAuthHooks } from "./api-client.ts";
import { ApiError, errorMessage, HTTP } from "./api-error.ts";
import { API_BASE } from "./api-routes.ts";
import {
  AUTH_BASE_URL,
  AUTH_CALLBACK_PATH,
  AUTH_REDIRECT_URI,
  AUTO_SIGN_IN_COOLDOWN_MS,
  missingAuthConfig,
  SIGN_IN_ATTEMPT_TTL_MS,
} from "./auth-config.ts";
import { AUTH_TIMEOUT_MS } from "./limits.ts";
import { navigateAway } from "./navigate.ts";
import { codeChallengeFor, randomToken } from "./pkce.ts";

/**
 * ===========================================================================
 * LA SESIÓN (§10.3)
 * ===========================================================================
 *
 * El login es el SSO de lala-ms con PKCE, y el API de ccmas hace de BFF:
 * canjea el `code`, guarda el refresh token en una cookie `httpOnly` que este
 * código nunca ve, y devuelve SOLO el access token. Ese token vive aquí, en
 * memoria, y en ningún otro sitio: ni `localStorage` ni `sessionStorage`. Al
 * recargar se pierde, y `/auth/refresh` lo recupera con la cookie.
 *
 * Esto no es React a propósito: el cliente HTTP y el router también necesitan
 * saber de la sesión, y no viven dentro de un componente. `app/session.tsx` es
 * la capa fina que lo conecta con React.
 *
 * Lo que se copia de `lala-ms/home-ui/docs/SSO.md`, porque cada punto les
 * costó un incidente: refresh single-flight; si el SSO falla, no se reintenta
 * en bucle; `redirect_uri` fijo por entorno; logout como navegación.
 */

/**
 * Quién está dentro, según `GET /me`.
 *
 * Se valida al llegar, en vez de fiarse del tipo: los permisos deciden qué se
 * enseña, y un campo renombrado en el API tiene que fallar aquí, a la vista,
 * y no dejar a todo el mundo sin botones sin que nadie sepa por qué.
 * TODO(api:types): cuando el API publique `/me`, comprobar el esquema contra
 * el generado con `satisfies`.
 */
const meSchema = z.object({
  userId: z.string(),
  email: z
    .string()
    .nullish()
    .transform((value) => value ?? ""),
  username: z
    .string()
    .nullish()
    .transform((value) => value ?? ""),
  fullName: z
    .string()
    .nullish()
    .transform((value) => value ?? ""),
  roles: z.array(z.string()),
  permissions: z.array(z.string()),
});

export type Me = z.infer<typeof meSchema>;

/**
 * - `anonymous`: no hay sesión. Con `reason`, porque algo falló y hay que
 *   decírselo a la persona en vez de mandarla otra vez al SSO.
 * - `authenticating`: se está recuperando o creando.
 * - `authenticated`: hay token y se sabe quién es.
 * - `expired`: la había y no se pudo renovar. Sin `reason` se está volviendo
 *   al SSO; con `reason`, el anti-bucle lo impidió.
 * - `unreachable`: no se llegó al servidor --sin red, sin respuesta a tiempo,
 *   o un 5xx--. NO es "sin sesión": ir al SSO no lo arregla. Se dice, con la
 *   referencia del fallo si la hay, y se puede reintentar.
 */
export type SessionState =
  | { readonly status: "anonymous"; readonly reason?: string }
  | { readonly status: "authenticating" }
  | { readonly status: "authenticated"; readonly me: Me }
  | { readonly status: "expired"; readonly reason?: string }
  | {
      readonly status: "unreachable";
      readonly reason: string;
      /** El `errorId` del fallo, para buscarlo en los logs. */
      readonly reference?: string;
    };

let token: string | undefined;
let state: SessionState = { status: "anonymous" };
/** Se está cerrando la sesión: nada de refrescar ni de relanzar el SSO. */
let leaving = false;

const listeners = new Set<() => void>();

const setState = (next: SessionState): void => {
  state = next;
  for (const listener of listeners) listener();
};

export const getSession = (): SessionState => state;

export const subscribeSession = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * El token en crudo. Lo piden el dictado en directo (lo manda en el primer
 * mensaje del WebSocket) y las conexiones por SSE del microservicio viejo
 * (`lib/sse.ts`): ni un WebSocket ni un `EventSource` admiten cabeceras, así
 * que no pueden llevar el `Authorization` de siempre.
 */
export const currentToken = (): string | undefined => token;

/**
 * Se pasa como funcion, no como objeto: el cliente la evalua en cada
 * peticion, asi que un token renovado se usa sin recrear nada.
 */
export const authHeaders = (): Record<string, string> =>
  token === undefined ? {} : { Authorization: `Bearer ${token}` };

// ---------------------------------------------------------------------------
// De dónde sale el token
// ---------------------------------------------------------------------------

interface IssuedToken {
  readonly accessToken: string;
  readonly expiresIn: number;
}

/** Una llamada de la sesión que no contestó a tiempo (`AUTH_TIMEOUT_MS`). */
export class AuthTimeoutError extends Error {
  constructor() {
    super(
      `El servidor no respondió en ${String(AUTH_TIMEOUT_MS / 1000)} segundos.`,
    );
    this.name = "AuthTimeoutError";
  }
}

/**
 * Con tope de tiempo. Sin él, una llamada que el gateway mantiene abierta
 * deja la aplicación esperando hasta que él se canse (diez minutos en
 * producción). Se corta la petición de verdad (`AbortController`), no solo
 * la espera.
 */
const withTimeout = async <T>(
  call: (signal: AbortSignal) => Promise<T>,
): Promise<T> => {
  const controller = new AbortController();
  const timer = setTimeout(() => {
    controller.abort();
  }, AUTH_TIMEOUT_MS);
  try {
    return await call(controller.signal);
  } catch (cause) {
    if (controller.signal.aborted) throw new AuthTimeoutError();
    throw cause;
  } finally {
    clearTimeout(timer);
  }
};

/**
 * `/auth/*` va con `credentials: "include"`: es lo que hace que la cookie del
 * refresh viaje. Funciona entre `ccmas.101grados.tech` y
 * `epic-api-v2.101grados.tech` porque son el mismo *site* y la cookie es
 * `SameSite=Lax` (§10.3).
 */
const authCall = <T>(path: string, body?: unknown): Promise<T> =>
  withTimeout((signal) =>
    request<T>(`${API_BASE}/auth/${path}`, {
      method: "POST",
      credentials: "include",
      signal,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }),
  );

const issueToken = async (): Promise<string> =>
  (await authCall<IssuedToken>("refresh")).accessToken;

let refreshing: Promise<boolean> | null = null;
/** Por qué falló el último refresh; `restoreSession` decide con él. */
let refreshFailure: unknown;

/**
 * Renueva el access token. SINGLE-FLIGHT: todas las peticiones que reciben un
 * 401 a la vez esperan al mismo refresh. Sin esto, cada una rotaría el refresh
 * token y dejaría a las demás con uno ya revocado.
 */
export const refreshSession = (): Promise<boolean> => {
  if (leaving) return Promise.resolve(false);
  refreshing ??= issueToken()
    .then((issued) => {
      token = issued;
      refreshFailure = undefined;
      return true;
    })
    .catch((cause: unknown) => {
      token = undefined;
      refreshFailure = cause;
      return false;
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
};

/**
 * Los códigos con los que `/auth/refresh` dice "no hay sesión que recuperar":
 * sin cookie, o con una que ya no vale. Es lo normal la primera vez, y solo
 * con ellos se va al SSO sin decir nada.
 */
const NO_SESSION_CODES: ReadonlySet<string> = new Set([
  "NO_SESSION",
  "SESSION_EXPIRED",
]);

const isNoSession = (cause: unknown): boolean =>
  cause instanceof ApiError &&
  cause.status === HTTP.unauthorized &&
  NO_SESSION_CODES.has(cause.code);

/**
 * Si el fallo es de NO LLEGAR al servidor, y no una respuesta suya: sin red
 * (`fetch` rechaza con `TypeError`), sin respuesta a tiempo, o un 5xx del API
 * o del gateway. Con su referencia, si el servidor la dio.
 */
export const unreachableOf = (
  cause: unknown,
): { readonly reason: string; readonly reference?: string } | null => {
  if (cause instanceof AuthTimeoutError) return { reason: cause.message };
  if (cause instanceof ApiError) {
    if (cause.status < HTTP.internal) return null;
    return cause.errorId === undefined
      ? { reason: cause.message }
      : { reason: cause.message, reference: cause.errorId };
  }
  if (cause instanceof TypeError) {
    return { reason: "No hay conexión con el servidor." };
  }
  return null;
};

/**
 * Cómo queda la sesión si el refresh del arranque falló.
 *
 * Cualquier fallo que no sea "no hay sesión" --el API caído, o uno que no
 * ofrece el login y contesta a `/auth/*` como si fuera una ruta protegida
 * ("Falta la cabecera Authorization")-- se DICE: rebotar contra el SSO no lo
 * arregla, y la persona acababa en una vuelta sin salida con un mensaje que
 * no explicaba nada.
 */
const restoreFailed = (cause: unknown): SessionState => {
  if (cause === undefined || isNoSession(cause)) return { status: "anonymous" };
  const unreachable = unreachableOf(cause);
  if (unreachable !== null) return { status: "unreachable", ...unreachable };
  return {
    status: "anonymous",
    reason: `No se pudo recuperar la sesión: ${errorMessage(cause)}`,
  };
};

const loadMe = async (): Promise<Me> =>
  meSchema.parse(
    await withTimeout((signal) =>
      request<unknown>(`${API_BASE}/me`, { signal }),
    ),
  );

// ---------------------------------------------------------------------------
// Recuperar la sesión al arrancar
// ---------------------------------------------------------------------------

let restoring: Promise<SessionState> | null = null;

/**
 * Recupera la sesión si la hay, con la cookie del refresh. No manda a nadie
 * al SSO; eso lo decide `ensureSession`.
 */
export const restoreSession = (): Promise<SessionState> => {
  if (state.status === "authenticated") return Promise.resolve(state);

  restoring ??= (async (): Promise<SessionState> => {
    const missing = missingAuthConfig();
    if (missing !== null) {
      setState({
        status: "anonymous",
        reason: `Falta configurar ${missing}: sin eso no se sabe a dónde ir a iniciar sesión.`,
      });
      return state;
    }

    setState({ status: "authenticating" });

    if (!(await refreshSession())) {
      setState(restoreFailed(refreshFailure));
      return state;
    }

    try {
      setState({ status: "authenticated", me: await loadMe() });
      clearAutoSignIn();
    } catch (cause) {
      token = undefined;
      const unreachable = unreachableOf(cause);
      setState(
        unreachable === null
          ? {
              status: "anonymous",
              reason: `No se pudo saber quién eres: ${errorMessage(cause)}`,
            }
          : { status: "unreachable", ...unreachable },
      );
    }
    return state;
  })().finally(() => {
    restoring = null;
  });

  return restoring;
};

/**
 * Lo que hace la ruta autenticada al entrar: recuperar la sesión y, si no
 * hay, ir al SSO. Devuelve el estado final, que puede ser "yéndose al SSO".
 * También es «Reintentar»: si no se llegó al servidor, vuelve a probar.
 *
 * Solo "no hay sesión" va al SSO. Un fallo de red o del servidor no: ahí no
 * hay nada que el SSO arregle.
 */
export const ensureSession = async (
  returnTo: string,
): Promise<SessionState> => {
  const restored = await restoreSession();
  if (restored.status === "anonymous" && restored.reason === undefined) {
    await signIn({ returnTo, automatic: true });
  }
  return state;
};

// ---------------------------------------------------------------------------
// Ir al SSO y volver
// ---------------------------------------------------------------------------

const ATTEMPT_KEY = "ccmas.auth.attempt";
const AUTO_SIGN_IN_KEY = "ccmas.auth.auto-sign-in";

/**
 * El intento de login en curso. En `sessionStorage` y no en memoria porque
 * tiene que sobrevivir a la ida y vuelta al SSO; y no en `localStorage`
 * porque es de esta pestaña, y otra pestaña empezando su propio login lo
 * pisaría.
 */
interface SignInAttempt {
  readonly verifier: string;
  readonly state: string;
  readonly returnTo: string;
  readonly createdAt: number;
}

const storage = {
  read: (key: string): string | null => {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  write: (key: string, value: string): void => {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Sin almacenamiento el login fallará al volver, y lo dirá.
    }
  },
  remove: (key: string): void => {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Nada que limpiar.
    }
  },
};

const takeAttempt = (): SignInAttempt | null => {
  const raw = storage.read(ATTEMPT_KEY);
  storage.remove(ATTEMPT_KEY);
  if (raw === null) return null;
  try {
    const attempt = JSON.parse(raw) as SignInAttempt;
    return Date.now() - attempt.createdAt > SIGN_IN_ATTEMPT_TTL_MS
      ? null
      : attempt;
  } catch {
    return null;
  }
};

/**
 * Solo rutas de esta aplicación. Un `returnTo` que empiece por `//` o traiga
 * esquema llevaría a otra web después de entrar: una redirección abierta.
 */
const safeReturnTo = (path: string): string =>
  path.startsWith("/") &&
  !path.startsWith("//") &&
  !path.startsWith(AUTH_CALLBACK_PATH)
    ? path
    : "/";

/** El anti-bucle (lección 5 de SSO.md): el SSO solo se lanza solo una vez. */
const recentlyAutoSignedIn = (): boolean => {
  const at = Number(storage.read(AUTO_SIGN_IN_KEY));
  return Number.isFinite(at) && Date.now() - at < AUTO_SIGN_IN_COOLDOWN_MS;
};

const clearAutoSignIn = (): void => {
  storage.remove(AUTO_SIGN_IN_KEY);
};

/**
 * Manda al navegador al SSO.
 *
 * `automatic` es cuando lo decide la aplicación --no hay sesión, o se perdió--
 * y no la persona. Solo en ese caso se mira el anti-bucle: si ya se lanzó
 * solo hace nada y aquí estamos otra vez, algo está roto y rebotar contra el
 * SSO no lo arregla. Se para y se enseña una pantalla con un botón.
 */
export const signIn = async ({
  returnTo,
  automatic = false,
}: {
  readonly returnTo: string;
  readonly automatic?: boolean;
}): Promise<void> => {
  if (automatic && recentlyAutoSignedIn()) {
    setState({
      status: state.status === "expired" ? "expired" : "anonymous",
      reason:
        "No se pudo iniciar sesión. Se intentó hace un momento y volvió a fallar.",
    });
    return;
  }
  if (automatic) storage.write(AUTO_SIGN_IN_KEY, String(Date.now()));

  const verifier = randomToken();
  const attempt: SignInAttempt = {
    verifier,
    state: randomToken(16),
    returnTo: safeReturnTo(returnTo),
    createdAt: Date.now(),
  };
  storage.write(ATTEMPT_KEY, JSON.stringify(attempt));

  const url = new URL(`${AUTH_BASE_URL}/api/auth/sso/authorize`);
  url.searchParams.set("redirect_uri", AUTH_REDIRECT_URI);
  url.searchParams.set("code_challenge", await codeChallengeFor(verifier));
  url.searchParams.set("code_challenge_method", "S256");
  url.searchParams.set("state", attempt.state);

  navigateAway(url.toString());
};

export interface CallbackParams {
  readonly code?: string | undefined;
  readonly state?: string | undefined;
  readonly error?: string | undefined;
}

let completing: { code: string; result: Promise<string | null> } | null = null;

/**
 * La vuelta del SSO: valida el `state`, canjea el `code` en el API y carga
 * quién es. Devuelve a dónde volver, o `null` si falló (y el estado lo dice).
 *
 * Idempotente por `code`: en desarrollo React monta dos veces, y el segundo
 * intento encontraría el intento ya consumido y taparía el éxito con un error.
 */
export const completeSignIn = (
  params: CallbackParams,
): Promise<string | null> => {
  const code = params.code ?? "";
  if (completing?.code === code) return completing.result;

  const result = (async (): Promise<string | null> => {
    setState({ status: "authenticating" });
    const attempt = takeAttempt();

    const fail = (reason: string): null => {
      token = undefined;
      setState({ status: "anonymous", reason });
      return null;
    };

    if (params.error !== undefined) {
      return fail(
        `El inicio de sesión respondió con un error: ${params.error}.`,
      );
    }
    if (attempt === null || code === "" || attempt.state !== params.state) {
      return fail(
        "El intento de inicio de sesión no es válido o caducó. Vuelve a intentarlo.",
      );
    }

    try {
      token = (
        await authCall<IssuedToken>("session", {
          code,
          codeVerifier: attempt.verifier,
          // El mismo con el que se pidió el código: auth lo exige idéntico
          // al canjear. Lo manda el front para que el API no tenga una
          // variable por cada admin.
          redirectUri: AUTH_REDIRECT_URI,
        })
      ).accessToken;
      setState({ status: "authenticated", me: await loadMe() });
      clearAutoSignIn();
      return attempt.returnTo;
    } catch (cause) {
      return fail(`No se pudo iniciar sesión: ${errorMessage(cause)}`);
    }
  })();

  completing = { code, result };
  return result;
};

// ---------------------------------------------------------------------------
// Perder la sesión y cerrarla
// ---------------------------------------------------------------------------

/** La ruta en la que está la persona, para volver a ella tras el SSO. */
const here = (): string =>
  `${window.location.pathname}${window.location.search}`;

/**
 * El cliente no pudo recuperar la sesión: se relanza el SSO una vez. Solo si
 * la había: un 401 mientras se arranca o se entra lo trata quien arranca.
 */
const sessionLost = (): void => {
  if (leaving || state.status !== "authenticated") return;
  /* Si el refresh no llegó al servidor, la sesión no se perdió: se sigue
     dentro, la llamada dice su fallo y la siguiente vuelve a probar. */
  if (unreachableOf(refreshFailure) !== null) return;
  token = undefined;
  setState({ status: "expired" });
  void signIn({ returnTo: here(), automatic: true });
};

/**
 * El logout del SSO, armado aquí como el `authorize`: el front sabe la URL
 * pública de auth (`VITE_AUTH_BASE_URL`); el API solo conoce la interna.
 */
const logoutUrl = (): string => {
  const url = new URL(`${AUTH_BASE_URL}/api/auth/sso/logout`);
  url.searchParams.set(
    "post_logout_redirect_uri",
    new URL(AUTH_REDIRECT_URI).origin,
  );
  return url.toString();
};

/**
 * Cierra la sesión: el API borra la cookie y el navegador NAVEGA al logout
 * del SSO, porque la sesión del SSO vive en una cookie de su dominio que un
 * `fetch` no manda (lección 2 de SSO.md). Si el API no contesta, se sale
 * igual: la cookie del refresh caduca sola.
 *
 * `beforeLeave` es lo que la interfaz tiene que soltar antes: el turno del
 * chat, la caché de otro usuario.
 */
export const signOut = async (beforeLeave: () => void): Promise<void> => {
  try {
    await authCall<unknown>("logout");
  } catch {
    // Sin API se sale igual: ver arriba.
  }

  leaving = true;
  token = undefined;
  beforeLeave();
  navigateAway(logoutUrl());
};

setAuthHooks({
  headers: authHeaders,
  refresh: refreshSession,
  lost: sessionLost,
});
